import { useEffect, useState } from "react";

/**
 * Twee vragen die vaak door elkaar lopen, en die je uit elkaar moet houden:
 *
 *   • hoe breed is het scherm?  → bepaalt de indeling
 *   • is het een aanraakscherm? → bepaalt de bediening
 *
 * Een telefoon is allebei, en daardoor lijkt het alsof breedte genoeg is. Een tablet laat zien dat
 * dat niet klopt: een iPad is 810 of 1024 pixels breed — ruim boven elke telefoongrens — maar heeft
 * geen muisaanwijzer. Alles wat op aanraken stukloopt (knopjes die pas bij hover verschijnen,
 * dubbelklikken, slepen) is daar net zo goed stuk. Andersom moet een smal browservenster op een
 * desktop juist niets van die aanraak-aanpassingen krijgen: daar is de muis er gewoon.
 *
 * Vandaar twee haken. Gebruik {@link useSmalScherm} voor indeling en {@link useAanraakscherm} voor
 * bediening; alles wat puur opmaak is hoort in een media query, niet hier.
 */
const TELEFOON = "(max-width: 768px)";

/** Aanwijsapparaat zonder fijne punt: een vinger. `pointer` kijkt naar de primaire invoer, dus een
 *  laptop met muis én touchscreen valt hier niet onder. */
const AANRAAK = "(pointer: coarse)";

function volgtMediaQuery(vraag: string): boolean {
  const [waar, setWaar] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(vraag);
    const pas = () => setWaar(mq.matches);
    pas();
    mq.addEventListener("change", pas);
    return () => mq.removeEventListener("change", pas);
  }, [vraag]);

  return waar;
}

/**
 * Draait dit op telefoonformaat? Dezelfde grens als de mobiele opmaaklaag van de host (768 pixels),
 * zodat wat de opmaak anders neerzet en wat de code anders doet niet uit elkaar kunnen lopen.
 *
 * Zonder matchMedia (server, of een test zonder stub) is het antwoord false: de brede weergave is de
 * norm en blijft dat zonder deze haak.
 */
export function useSmalScherm(): boolean {
  return volgtMediaQuery(TELEFOON);
}

/**
 * Wordt dit scherm met een vinger bediend? Voor alles wat op een aanraakscherm niet bestaat —
 * hover, dubbelklikken, slepen — ongeacht hoe breed het scherm is. Een tablet valt hieronder, een
 * desktop met muis niet, ook niet als het venster smal staat.
 */
export function useAanraakscherm(): boolean {
  return volgtMediaQuery(AANRAAK);
}
